/**
 * Persistent banner naming the ERP environment(s) the UI is talking to
 * whenever at least one registry is **not** tagged `production`.
 *
 * The safety property is "no banner ⇒ production", so an untagged (`null`)
 * environment also raises the banner ("Environnement non renseigné"): tagging
 * the registry `production` in the admin UI is what silences it.
 *
 * Rendered as a **single compact line**: the environment title, then every
 * non-production registry (`name (slug) — label — base URL`), joined by
 * `ERP_ENVIRONMENT_BANNER_COPY.compactLine`. That whole string is the Alert's
 * `title` and the Alert has no children, so it renders as one semibold amber
 * line; `py-2` trims the Alert's default `p-4` down to a slim bar.
 *
 * Presentational only — the caller supplies the registries. Every string comes
 * from `ERP_ENVIRONMENT_BANNER_COPY` / `ERP_ENVIRONMENT_LABELS`.
 *
 * Not registered as a configurable component: this is pure chrome/infra with
 * no tenant-facing fields, the one opt-out allowed by kusudama's CLAUDE.md
 * ("Configurable Components").
 */

import { Alert } from '@components/base/feedback';

import {
  ERP_ENVIRONMENT_BANNER_COPY,
  ERP_ENVIRONMENT_LABELS,
  getErpEnvironmentLabel,
  type ErpEnvironment,
} from './erpEnvironment';

/** The registry fields the banner needs — a subset of the API payload. */
export interface ErpEnvironmentBannerRegistry {
  slug: string;
  name: string;
  /** The ERP host the registry points to, as stored (API snake_case). */
  base_url: string;
  environment: ErpEnvironment | null;
}

interface ErpEnvironmentBannerProps {
  registries: ReadonlyArray<ErpEnvironmentBannerRegistry>;
}

/** Title for the shared environment of every listed registry. */
function getBannerTitle(environment: ErpEnvironment | null): string {
  return environment === null
    ? ERP_ENVIRONMENT_BANNER_COPY.titleUnset
    : ERP_ENVIRONMENT_BANNER_COPY.titleSingle(
        ERP_ENVIRONMENT_LABELS[environment],
      );
}

/**
 * Renders nothing when every registry is tagged `production` (or the list is
 * empty); otherwise one slim warning `Alert` whose single line names the
 * environment and every non-production registry.
 */
export function ErpEnvironmentBanner({
  registries,
}: Readonly<ErpEnvironmentBannerProps>) {
  const nonProduction = registries.filter(
    (registry) => registry.environment !== 'production',
  );

  if (nonProduction.length === 0) {
    return null;
  }

  const environments = [
    ...new Set(nonProduction.map((registry) => registry.environment)),
  ];
  // `undefined` marks "several environments" — `null` is itself a value here.
  const sharedEnvironment =
    environments.length === 1 ? environments[0] : undefined;
  const title =
    sharedEnvironment === undefined
      ? ERP_ENVIRONMENT_BANNER_COPY.titleMixed
      : getBannerTitle(sharedEnvironment);

  const line = ERP_ENVIRONMENT_BANNER_COPY.compactLine(
    title,
    nonProduction.map((registry) =>
      ERP_ENVIRONMENT_BANNER_COPY.registryLine(
        registry.name,
        registry.slug,
        getErpEnvironmentLabel(registry.environment),
        registry.base_url,
      ),
    ),
  );

  return (
    <Alert
      variant="warning"
      title={line}
      aria-label={ERP_ENVIRONMENT_BANNER_COPY.ariaLabel}
      data-testid="erp-environment-banner"
      className="rounded-none py-2"
    />
  );
}
